/**
 * Groups Service - API integration for groups (organizations)
 * Follows pattern from computeService.ts
 */

import { type ApiError } from '../types/api'
import type {
  CreateGlobalGroupRequest,
  CreateGroupRequest,
  Group,
  GroupMembership,
  GroupMembersResponse,
  GroupsResponse,
  GroupWithTenant,
  UpdateGroupRequest,
} from '../types/api/groups'
import type { Tenant } from '../types/tenant'

// Configuration
const API_BASE_URL = '/api' // Use relative URL to go through Vite proxy

/**
 * Fetch groups from the API with optional filters
 */
export const fetchGroups = async (
  tenant: Tenant,
  filters?: {
    limit?: number
    offset?: number
    group_type?: string
    include_deleted?: boolean
    query?: string
  },
): Promise<GroupsResponse> => {
  const url = new URL(
    `${API_BASE_URL}/${tenant.id}/groups`,
    window.location.origin,
  )

  if (filters?.limit) url.searchParams.set('limit', filters.limit.toString())
  if (filters?.offset) url.searchParams.set('offset', filters.offset.toString())
  if (filters?.group_type)
    url.searchParams.set('group_type', filters.group_type)
  if (filters?.include_deleted) url.searchParams.set('include_deleted', 'true')
  if (filters?.query) url.searchParams.set('query', filters.query)

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      // TODO: Add Authorization header when auth is implemented
      // 'Authorization': `Bearer ${getAuthToken()}`,
    },
  })

  if (!response.ok) {
    let errorData: ApiError
    try {
      errorData = await response.json()
    } catch {
      errorData = {
        error: 'Network Error',
        message: `HTTP ${response.status}: ${response.statusText}`,
        code: response.status,
      }
    }
    throw new Error(`API Error: ${errorData.message}`)
  }

  const data: GroupsResponse = await response.json()
  return data
}

/**
 * Create a new group
 */
export const createGroup = async (
  tenant: Tenant,
  data: CreateGroupRequest,
): Promise<Group> => {
  const url = `${API_BASE_URL}/${tenant.id}/groups`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // TODO: Add Authorization header when auth is implemented
      // 'Authorization': `Bearer ${getAuthToken()}`,
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    let errorData: ApiError
    try {
      errorData = await response.json()
    } catch {
      errorData = {
        error: 'Network Error',
        message: `HTTP ${response.status}: ${response.statusText}`,
        code: response.status,
      }
    }
    throw new Error(`API Error: ${errorData.message}`)
  }

  const group: Group = await response.json()
  return group
}

/**
 * Update an existing group
 */
export const updateGroup = async (
  tenant: Tenant,
  groupId: string,
  data: UpdateGroupRequest,
): Promise<Group> => {
  const url = `${API_BASE_URL}/${tenant.id}/groups/${groupId}`

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      // TODO: Add Authorization header when auth is implemented
      // 'Authorization': `Bearer ${getAuthToken()}`,
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    let errorData: ApiError
    try {
      errorData = await response.json()
    } catch {
      errorData = {
        error: 'Network Error',
        message: `HTTP ${response.status}: ${response.statusText}`,
        code: response.status,
      }
    }
    throw new Error(`API Error: ${errorData.message}`)
  }

  const group: Group = await response.json()
  return group
}

/**
 * Delete a group (soft delete)
 */
export const deleteGroup = async (
  tenant: Tenant,
  groupId: string,
): Promise<void> => {
  const url = `${API_BASE_URL}/${tenant.id}/groups/${groupId}`

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      // TODO: Add Authorization header when auth is implemented
      // 'Authorization': `Bearer ${getAuthToken()}`,
    },
  })

  if (!response.ok) {
    let errorData: ApiError
    try {
      errorData = await response.json()
    } catch {
      errorData = {
        error: 'Network Error',
        message: `HTTP ${response.status}: ${response.statusText}`,
        code: response.status,
      }
    }
    throw new Error(`API Error: ${errorData.message}`)
  }
}

/**
 * Fetch members of a group with optional filters
 */
export const fetchGroupMembers = async (
  tenant: Tenant,
  groupId: string,
  filters?: {
    limit?: number
    offset?: number
    status?: string
    query?: string
  },
): Promise<GroupMembersResponse> => {
  const url = new URL(
    `${API_BASE_URL}/${tenant.id}/groups/${groupId}/users`,
    window.location.origin,
  )

  if (filters?.limit) url.searchParams.set('limit', filters.limit.toString())
  if (filters?.offset) url.searchParams.set('offset', filters.offset.toString())
  if (filters?.status) url.searchParams.set('status', filters.status)
  if (filters?.query) url.searchParams.set('query', filters.query)

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      // TODO: Add Authorization header when auth is implemented
      // 'Authorization': `Bearer ${getAuthToken()}`,
    },
  })

  if (!response.ok) {
    let errorData: ApiError
    try {
      errorData = await response.json()
    } catch {
      errorData = {
        error: 'Network Error',
        message: `HTTP ${response.status}: ${response.statusText}`,
        code: response.status,
      }
    }
    throw new Error(`API Error: ${errorData.message}`)
  }

  const data: GroupMembersResponse = await response.json()
  return data
}

/**
 * Add a user to a group
 */
export const addUserToGroup = async (
  tenant: Tenant,
  groupId: string,
  userId: string,
): Promise<GroupMembership> => {
  const url = `${API_BASE_URL}/${tenant.id}/groups/${groupId}/users/${userId}`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    let errorData: ApiError
    try {
      errorData = await response.json()
    } catch {
      errorData = {
        error: 'Network Error',
        message: `HTTP ${response.status}: ${response.statusText}`,
        code: response.status,
      }
    }
    throw new Error(`API Error: ${errorData.message}`)
  }

  const membership: GroupMembership = await response.json()
  return membership
}

/**
 * Remove a user from a group
 */
export const removeUserFromGroup = async (
  tenantSlug: string,
  groupId: string,
  userId: string,
): Promise<void> => {
  const url = `${API_BASE_URL}/${tenantSlug}/groups/${groupId}/users/${userId}`

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      // TODO: Add Authorization header when auth is implemented
      // 'Authorization': `Bearer ${getAuthToken()}`,
    },
  })

  if (!response.ok) {
    let errorData: ApiError
    try {
      errorData = await response.json()
    } catch {
      errorData = {
        error: 'Network Error',
        message: `HTTP ${response.status}: ${response.statusText}`,
        code: response.status,
      }
    }
    throw new Error(`API Error: ${errorData.message}`)
  }
}

// ============================================================================
// Global Group Operations (super_admin only)
// ============================================================================

/**
 * Fetch all groups across all tenants (super_admin only)
 */
export const fetchAllGroups = async (filters?: {
  limit?: number
  offset?: number
  group_type?: string
  include_deleted?: boolean
  query?: string
}): Promise<{
  items: GroupWithTenant[]
  pagination: GroupsResponse['pagination']
}> => {
  const url = new URL(`${API_BASE_URL}/groups`, window.location.origin)

  if (filters?.limit) url.searchParams.set('limit', filters.limit.toString())
  if (filters?.offset) url.searchParams.set('offset', filters.offset.toString())
  if (filters?.group_type)
    url.searchParams.set('group_type', filters.group_type)
  if (filters?.include_deleted) url.searchParams.set('include_deleted', 'true')
  if (filters?.query) url.searchParams.set('query', filters.query)

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      // TODO: Add Authorization header when auth is implemented
      // 'Authorization': `Bearer ${getAuthToken()}`,
    },
  })

  if (!response.ok) {
    let errorData: ApiError
    try {
      errorData = await response.json()
    } catch {
      errorData = {
        error: 'Network Error',
        message: `HTTP ${response.status}: ${response.statusText}`,
        code: response.status,
      }
    }
    throw new Error(`API Error: ${errorData.message}`)
  }

  const data = await response.json()
  return data
}

/**
 * Create a new group in any tenant (super_admin only)
 */
export const createGlobalGroup = async (
  data: CreateGlobalGroupRequest,
): Promise<Group> => {
  const url = `${API_BASE_URL}/groups`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // TODO: Add Authorization header when auth is implemented
      // 'Authorization': `Bearer ${getAuthToken()}`,
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    let errorData: ApiError
    try {
      errorData = await response.json()
    } catch {
      errorData = {
        error: 'Network Error',
        message: `HTTP ${response.status}: ${response.statusText}`,
        code: response.status,
      }
    }
    throw new Error(`API Error: ${errorData.message}`)
  }

  const group: Group = await response.json()
  return group
}

/**
 * Update an existing group globally (super_admin only)
 */
export const updateGlobalGroup = async (
  groupId: string,
  data: UpdateGroupRequest,
): Promise<Group> => {
  const url = `${API_BASE_URL}/groups/${groupId}`

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      // TODO: Add Authorization header when auth is implemented
      // 'Authorization': `Bearer ${getAuthToken()}`,
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    let errorData: ApiError
    try {
      errorData = await response.json()
    } catch {
      errorData = {
        error: 'Network Error',
        message: `HTTP ${response.status}: ${response.statusText}`,
        code: response.status,
      }
    }
    throw new Error(`API Error: ${errorData.message}`)
  }

  const group: Group = await response.json()
  return group
}

/**
 * Delete a group globally (super_admin only)
 */
export const deleteGlobalGroup = async (groupId: string): Promise<void> => {
  const url = `${API_BASE_URL}/groups/${groupId}`

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      // TODO: Add Authorization header when auth is implemented
      // 'Authorization': `Bearer ${getAuthToken()}`,
    },
  })

  if (!response.ok) {
    let errorData: ApiError
    try {
      errorData = await response.json()
    } catch {
      errorData = {
        error: 'Network Error',
        message: `HTTP ${response.status}: ${response.statusText}`,
        code: response.status,
      }
    }
    throw new Error(`API Error: ${errorData.message}`)
  }
}

/**
 * Add a user to a global group (super admin only)
 * Used for organization management where tenant context is not needed
 */
export const addUserToGlobalGroup = async (
  groupId: string,
  userId: string,
  role: string,
): Promise<GroupMembership> => {
  const url = `${API_BASE_URL}/groups/${groupId}/users/${userId}`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ role }),
  })

  if (!response.ok) {
    let errorData: ApiError
    try {
      errorData = await response.json()
    } catch {
      errorData = {
        error: 'Network Error',
        message: `HTTP ${response.status}: ${response.statusText}`,
        code: response.status,
      }
    }
    throw new Error(`API Error: ${errorData.message}`)
  }

  const membership: GroupMembership = await response.json()
  return membership
}

/**
 * Provision Neo4j tenant role and user for an organization (super_admin only)
 * Use cases:
 * - Backfilling organizations created before Neo4j provisioning was added
 * - Recovering from provisioning failures during organization creation
 * - Testing Neo4j provisioning independently
 */
export const provisionNeo4jForGroup = async (
  groupId: string,
): Promise<{
  message: string
  group_id: string
  group_slug: string
  group_name: string
}> => {
  const url = `${API_BASE_URL}/groups/${groupId}/provision-neo4j`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // TODO: Add Authorization header when auth is implemented
      // 'Authorization': `Bearer ${getAuthToken()}`,
    },
  })

  if (!response.ok) {
    let errorData: ApiError
    try {
      errorData = await response.json()
    } catch {
      errorData = {
        error: 'Network Error',
        message: `HTTP ${response.status}: ${response.statusText}`,
        code: response.status,
      }
    }
    throw new Error(`API Error: ${errorData.message}`)
  }

  const data = await response.json()
  return data
}
